import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) => {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(2)}`;
  }
};

const conditionFromTitle = (title = '') => {
  const suffix = title.split('|').pop()?.trim() || '';
  if (/manufacturer renewed/i.test(suffix)) return suffix;
  if (/new no box|new \(no box\)/i.test(suffix)) return 'New — no retail box';
  if (/open box/i.test(suffix)) return 'New — open box';
  if (/box damaged/i.test(suffix)) return 'New — box damaged';
  if (/brand new/i.test(suffix)) return 'Brand new';
  return suffix;
};

const requirementFor = (title = '') => {
  if (/SanDisk Ultra.*(?:microSD|Memory Card)/i.test(title)) {
    return 'Best for event recording; choose High Endurance media for 24/7 recording';
  }
  if (/Entry Sensor E20|Motion Sensor E20|Flood (and|&) Freeze Sensor E20|Indoor Siren E20|Key Fob E10|Panic Button E10/i.test(title)) {
    return 'One compatible HomeBase required';
  }
  if (/Add-On Camera/i.test(title)) return 'Check required hub/recorder';
  return '';
};

const exclusiveGroupFor = (title = '') => {
  if (/\bgenuine (?:corner|pole) mount\b/i.test(title)) return 'mounting-style';
  return '';
};

// These exact product IDs match the two approved native Shopify discount rules.
// Shopify's cart/checkout remains the pricing authority; no client-supplied price is sent.
const BUNDLE_PILOT = {
  cameras: new Set(['7814092226603', '7814340378667', '7814345457707', '7814092029995', '7814092128299']),
  hub: '7814091702315',
  drive: '7814091997227',
};

class CgauProductAssistant extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this.list = this.querySelector('[data-addon-list]');
    this.panel = this.querySelector('[data-addons]');
    this.button = this.querySelector('[data-bundle-add]');
    this.status = this.querySelector('[data-addon-status]');
    this.more = this.querySelector('[data-extras-more]');
    this.offer = this.querySelector('[data-bundle-offer]');
    this._onMore = () => {
      this._expanded = !this._expanded;
      this.updateVisibleOptions();
    };
    this.more?.addEventListener('click', this._onMore);
    this._onBundleAdd = () => this.addBundle();
    this._onChange = (event) => {
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      if (input?.checked && input.dataset.exclusiveGroup) {
        const group = CSS.escape(input.dataset.exclusiveGroup);
        this.querySelectorAll(`[data-addon-list] input[data-exclusive-group="${group}"]`).forEach((candidate) => {
          if (candidate !== input) candidate.checked = false;
        });
      }
      this.updateButton();
    };
    this.button?.addEventListener('click', this._onBundleAdd);
    this.addEventListener('change', this._onChange);
    this._onMainQuantityChange = (event) => {
      if (event.target instanceof Element && event.target.closest('product-form-component') === this.currentProductForm()) {
        this.updateButton();
      }
    };
    for (const name of ['input', 'change', 'quantity-selector:update']) {
      this.ownerDocument.addEventListener(name, this._onMainQuantityChange);
    }
    if (this.dataset.recommendationsLoaded !== 'true') this.loadRecommendations();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this._onBundleAdd);
    this.more?.removeEventListener('click', this._onMore);
    this.removeEventListener('change', this._onChange);
    for (const name of ['input', 'change', 'quantity-selector:update']) {
      this.ownerDocument.removeEventListener(name, this._onMainQuantityChange);
    }
    this._connected = false;
  }

  async loadRecommendations() {
    if (!this.dataset.productId || !this.list || !this.panel) return;

    try {
      const embedded = this.querySelector('[data-compatible-extras]');
      if (embedded) {
        const payload = JSON.parse(embedded.textContent);
        if (payload.version !== 1 || !Array.isArray(payload.rules?.items) || !Array.isArray(payload.products)) {
          throw new Error('Invalid compatible extras data');
        }
        const products = new Map(payload.products.map((product) => [String(product.id), product]));
        const seen = new Set();
        for (const rule of payload.rules.items) {
          const id = String(rule.product_id);
          const product = products.get(id);
          if (seen.has(id) || id === this.dataset.productId || !product) continue;
          seen.add(id);
          const variants = (product.variants || []).filter((variant) => variant.available && Number(variant.price) > 0);
          if (variants.length) this.list.append(this.buildOption(product, variants, rule));
        }
        this.dataset.recommendationsLoaded = 'true';
        this.panel.hidden = this.list.children.length === 0;
        this.updateVisibleOptions();
        this.updateOffer();
        return;
      }
      // New/unreviewed products fail closed until a verified relationship list exists.
      this.dataset.recommendationsLoaded = 'true';
    } catch (error) {
      console.warn('[CamGuard add-ons]', error);
    }
  }

  buildOption(product, variants, rule = null) {
    const variant = variants[0];
    const row = document.createElement('div');
    row.className = 'cgau-addon';
    row.dataset.choiceGroup = rule?.choice_group || String(product.id);

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = `CgauAddon-${this.id}-${product.id}`.replace(/[^a-zA-Z0-9_-]/g, '-');
    input.value = String(variant.id);
    input.dataset.price = String(variant.price || 0);
    input.dataset.quantity = '1';
    input.dataset.productId = String(product.id);
    input.setAttribute('aria-label', `Add ${product.title}`);
    const exclusiveGroup = rule ? rule.exclusive_group : exclusiveGroupFor(product.title);
    if (exclusiveGroup) input.dataset.exclusiveGroup = exclusiveGroup;

    const imageLabel = document.createElement('label');
    imageLabel.className = 'cgau-addon__image-label';
    imageLabel.htmlFor = input.id;

    const image = document.createElement('img');
    image.className = 'cgau-addon__image';
    image.loading = 'lazy';
    image.width = 116;
    image.height = 116;
    image.alt = '';
    image.src = product.featured_image || product.images?.[0] || '';
    imageLabel.append(image);

    const copy = document.createElement('span');
    copy.className = 'cgau-addon__copy';
    const name = document.createElement('a');
    name.className = 'cgau-addon__name';
    name.textContent = product.title;
    name.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    copy.append(name);

    const meta = document.createElement('span');
    meta.className = 'cgau-addon__meta';
    const condition = product.condition || conditionFromTitle(product.title);
    const requirement = rule ? rule.reason : requirementFor(product.title);
    if (condition) meta.append(this.chip(condition));
    if (requirement) meta.append(this.chip(requirement, true));
    if (exclusiveGroup) meta.append(this.chip(exclusiveGroup === 'hub' ? 'Choose one hub' : 'Choose one mounting style', true));
    const pilotOffer = this.offerForProduct(String(product.id));
    if (pilotOffer) {
      const badge = this.chip(pilotOffer, false);
      badge.classList.add('cgau-chip--bundle');
      meta.append(badge);
    }
    const kitSize = /\b(\d+)[ -]?cam(?:era)? kit\b/i.exec(this.dataset.productTitle || '');
    if (kitSize && /microsd|memory card/i.test(product.title || '')) {
      meta.append(this.chip(`${kitSize[1]} cameras: choose Qty ${kitSize[1]} for one card each`, true));
    }
    copy.append(meta);

    const controls = document.createElement('span');
    controls.className = 'cgau-addon__controls';

    if (variants.length > 1 || variant.title !== 'Default Title') {
      const picker = document.createElement('select');
      picker.className = 'cgau-addon__variant';
      picker.setAttribute('aria-label', `Choose an option for ${product.title}`);
      variants.forEach((candidate) => {
        const option = document.createElement('option');
        option.value = String(candidate.id);
        option.dataset.price = String(candidate.price || 0);
        option.textContent = `${candidate.title} · ${money(candidate.price)}`;
        picker.append(option);
      });
      picker.addEventListener('change', () => {
        const selected = picker.selectedOptions[0];
        input.value = selected.value;
        input.dataset.price = selected.dataset.price || '0';
        updateQuantityLimit(variants.find((candidate) => String(candidate.id) === selected.value));
        price.textContent = money(input.dataset.price);
        this.updateButton();
      });
      controls.append(picker);
    }

    const quantity = document.createElement('select');
    quantity.className = 'cgau-addon__quantity';
    quantity.setAttribute('aria-label', `Choose quantity for ${product.title}`);
    const updateQuantityLimit = (candidate) => {
      const limit = Math.min(4, Math.max(1, Number(candidate?.stock || 1)));
      const chosen = Math.min(Number(input.dataset.quantity || 1), limit);
      quantity.replaceChildren();
      for (let count = 1; count <= limit; count += 1) {
        const option = document.createElement('option');
        option.value = String(count);
        option.textContent = `Qty ${count}`;
        quantity.append(option);
      }
      quantity.value = String(chosen);
      input.dataset.quantity = String(chosen);
    };
    updateQuantityLimit(variant);
    quantity.addEventListener('change', () => {
      input.dataset.quantity = quantity.value;
      this.updateButton();
    });
    controls.append(quantity);
    copy.append(controls);

    const price = document.createElement('span');
    price.className = 'cgau-addon__price';
    price.textContent = money(variant.price);

    row.append(input, imageLabel, copy, price);
    return row;
  }

  updateVisibleOptions() {
    if (!this.list) return;
    const rows = [...this.list.children];
    const groups = new Set();
    const featured = new Set();
    for (const row of rows) {
      if (featured.size < 3 && !groups.has(row.dataset.choiceGroup)) {
        groups.add(row.dataset.choiceGroup);
        featured.add(row);
      }
    }
    for (const row of rows) {
      // A selection must stay visible when the list is collapsed.
      row.hidden = !this._expanded && !featured.has(row) && !row.querySelector('input')?.checked;
    }
    if (this.more) {
      this.more.hidden = rows.length <= featured.size;
      this.more.setAttribute('aria-expanded', String(Boolean(this._expanded)));
      this.more.textContent = this._expanded ? 'Show suggested extras' : `View all ${rows.length} compatible extras`;
    }
  }

  offerForProduct(id) {
    if (this.dataset.bundlePilot !== 'true') return '';
    if (BUNDLE_PILOT.cameras.has(this.dataset.productId) && id === BUNDLE_PILOT.hub) return 'Save 5% on this HomeBase with this camera';
    if (this.dataset.productId === BUNDLE_PILOT.hub && id === BUNDLE_PILOT.drive) return 'Save 5% on this drive with HomeBase 3';
    if (this.dataset.productId === BUNDLE_PILOT.drive && id === BUNDLE_PILOT.hub) return 'Save 5% on the drive when bought together';
    if (this.dataset.productId === BUNDLE_PILOT.hub && BUNDLE_PILOT.cameras.has(id)) return 'Save 5% on the HomeBase when bought together';
    return '';
  }

  updateOffer() {
    if (!this.offer) return;
    const eligible = [...this.querySelectorAll('[data-addon-list] input')].some((input) => this.offerForProduct(input.dataset.productId));
    this.offer.hidden = !eligible;
    this.offer.textContent = eligible ? 'Bundle & save 5% on the marked item when purchased together. One discounted unit; one bundle offer per order. Automatically applied in your cart. Cannot be combined with other discounts.' : '';
  }

  chip(text, warning = false) {
    const chip = document.createElement('span');
    chip.className = `cgau-chip${warning ? ' cgau-chip--warning' : ''}`;
    chip.textContent = text;
    return chip;
  }

  selectedInputs() {
    return [...this.querySelectorAll('[data-addon-list] input[type="checkbox"]:checked')];
  }

  hubRecommendationAllowed(title) {
    // Camera kits and hub accessories are not standalone hub recommendations.
    if (!/homebase/i.test(title) || /camera|\b(?:kit|mount|cable|adapter|adaptor|stand|cover)\b/i.test(title)) return true;
    const hub = title.match(/homebase\s+(professional\s+s1|mini|3|2)\b/i)?.[0].toLowerCase().replace(/\s+/g, ' ');
    try {
      const hubs = JSON.parse(this.dataset.compatibleHubs || '[]');
      return Boolean(hub && Array.isArray(hubs) && hubs.some((item) => String(item).toLowerCase().trim() === hub));
    } catch (_) {
      return false;
    }
  }

  currentProductForm() {
    const selector = `product-form-component[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`;
    return this.closest('.shopify-section')?.querySelector(selector) || this.ownerDocument.querySelector(selector);
  }

  currentQuantity() {
    const input = this.currentProductForm()?.querySelector('input[name="quantity"]');
    if (!input) return 1;
    const quantity = Number(input.value);
    return Number.isSafeInteger(quantity) && quantity > 0 && input.checkValidity() ? quantity : null;
  }

  updateButton() {
    if (!this.button || this._adding) return;
    const selected = this.selectedInputs();
    const selectedUnits = selected.reduce((sum, input) => sum + Number(input.dataset.quantity || 1), 0);
    const extrasTotal = selected.reduce(
      (sum, input) => sum + Number(input.dataset.price || 0) * Number(input.dataset.quantity || 1),
      0
    );
    const quantity = this.currentQuantity();
    if (quantity === null) {
      this.button.disabled = true;
      this.button.textContent = 'Enter a valid product quantity';
      return;
    }
    const total = Number(this.dataset.currentPrice || 0) * quantity + extrasTotal;
    const possibleOffer = selected.some((input) => this.offerForProduct(input.dataset.productId));
    this.button.disabled = !selected.length || this.dataset.productAvailable !== 'true';
    this.button.textContent = selected.length
      ? `Add ${quantity} ${quantity === 1 ? 'product' : 'products'} + ${selectedUnits} ${selectedUnits === 1 ? 'extra' : 'extras'} · ${money(total)}${possibleOffer ? ' before bundle saving' : ''}`
      : 'Select an extra to build your setup';
  }

  currentVariant() {
    return this.currentProductForm()?.querySelector('input[name="id"]')?.value;
  }

  async addBundle() {
    const variantId = this.currentVariant();
    const selected = this.selectedInputs();
    const quantity = this.currentQuantity();
    if (!variantId || !selected.length || !this.button || quantity === null || this._adding || this.dataset.productAvailable !== 'true') return;

    const items = [
      { id: Number(variantId), quantity },
      ...selected.map((input) => ({ id: Number(input.value), quantity: Number(input.dataset.quantity || 1) })),
    ];
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    this._adding = true;
    this.button.disabled = true;
    this.button.textContent = 'Adding your setup…';
    this.setStatus('', 'loading');

    let added = false;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Could not add this setup');
      added = true;
      // Once Shopify has added the lines, prevent an accidental duplicate retry
      // even if the subsequent cart read fails.
      selected.forEach((input) => (input.checked = false));

      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      if (!cartResponse.ok || !Array.isArray(cart.items)) throw new Error('Items were added, but the cart total could not refresh. Open your cart to check them.');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cgau-product-assistant',
          sourceId: this.id,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          didError: false,
        },
      });
      const discount = Number(cart.total_discount || 0);
      this.setStatus(`Your product and selected extras are in the cart. Cart total: ${money(cart.total_price)}${discount > 0 ? `; cart savings: ${money(discount)}` : ''}.`, 'success');
    } catch (error) {
      deferred.reject(error);
      // A stock change or interrupted response can leave part of an attempted
      // addition in Shopify's cart. Require an explicit new selection on retry.
      selected.forEach((input) => (input.checked = false));
      this.setStatus(added ? 'Items were added, but the cart total could not refresh. Open your cart to check them.' : `${error instanceof Error ? error.message : 'Could not confirm this setup.'} Check your cart before trying again; some items may already have been added.`, 'error');
    } finally {
      this._adding = false;
      this.updateButton();
    }
  }

  setStatus(message, state) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;
  }
}

if (!customElements.get('cgau-product-assistant')) {
  customElements.define('cgau-product-assistant', CgauProductAssistant);
}

const initReviewJump = () => {
  const link = document.querySelector('[data-review-jump]');
  if (!link) return;
  const target = document.getElementById('cgau-product-reviews');
  if (!target) {
    link.style.display = 'none';
    return;
  }
  const apply = () => {
    const badge = link.querySelector('.jdgm-prev-badge, .jdgm-preview-badge');
    const cta = link.querySelector('[data-review-cta]');
    // Judge.me marks the stars role="button"; inside this link that would nest
    // interactive controls, so the stars become decorative and the link is the
    // single control.
    link.querySelectorAll('[role="button"], [tabindex]').forEach((el) => {
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-label');
    });
    if (!cta) return;
    const count = Number(badge?.dataset.numberOfReviews || 0);
    if (count > 0) {
      cta.textContent = `Read ${count} customer ${count === 1 ? 'review' : 'reviews'}`;
    } else {
      cta.textContent = 'No product reviews yet — be the first to review it';
      badge?.style.setProperty('display', 'none', 'important');
    }
  };
  apply();
  setTimeout(apply, 1500);
  // The theme scrolls inside .page-wrapper (body is overflow:hidden), so native
  // fragment navigation never moves the page — scroll explicitly instead.
  link.addEventListener('click', (event) => {
    event.preventDefault();
    try {
      history.pushState(null, '', '#cgau-product-reviews');
    } catch (_) {
      /* ignore */
    }
    target.focus({ preventScroll: true });
    // 'instant' — smooth scrolling is unreliable across this theme's very long
    // scroll container; a direct jump always lands.
    target.scrollIntoView({ behavior: 'instant', block: 'start' });
  });
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReviewJump);
} else {
  initReviewJump();
}
